/**
 * Real, OAuth-backed payslip/salary-disbursement-file/payment-source
 * -account/statutory-payment/filing-calendar hook for `PostRunStep` (C8,
 * #22330; C9, #22331) — self-fetching-hook-shaped, but action-triggered
 * rather than boot-time, since every method here is a user-initiated
 * download/generate/record action, not a list this screen needs loaded up
 * front (same "self-fetching, not a `dev_*.ts` boot-time seed" stance as
 * `use_my_pay.ts`/`use_session_facts.ts`).
 *
 * `usePayRunPostActions()` resolves `{ firm_id, entity_id }` from
 * `useOAuthEntityContext()` (the shared C1 helper) and is `undefined` only
 * when that context itself isn't `'ready'` — in practice unreachable by the
 * time `main.tsx`'s `AuthGate` mounts `<WageProStoreProvider>`, since
 * `useSessionFacts()` (gating that mount) already requires the SAME grant to
 * be resolved. Kept `| undefined` anyway so this hook's own contract doesn't
 * silently assume that upstream invariant.
 *
 * C9 (#22331) migrated the LAST dev-bypass-only third of
 * `PayRunPostActionsController` (statutory payments, the filing calendar,
 * and the statutory-payment dialog's OWN bank-account picker) to a REAL,
 * OAuth-backed executor (`oauth_statutory_filing.ts`) — `dev_pay_run_post_
 * actions.ts` is DELETED. This hook therefore now builds the WHOLE
 * controller directly; there is no more "real subset + dev-bypass subset"
 * merge (`composePayRunPostActions` is gone). `main.tsx` no longer needs any
 * dev-bypass fan-out for post-run actions at all.
 *
 * ── `buildPostActionsController` (C9 second round, #22415) ───────────────
 *
 * The controller-construction logic is a pure, exported, dependency-injected
 * factory with NO React inside it — `usePayRunPostActions` itself is a thin
 * wrapper that resolves `{ firm_id, entity_id, writeCapable }` via the real
 * hooks and passes the real fetch functions in as `resolvers`. This is what
 * makes the wiring (which method gets which resolver, which methods go
 * `undefined` for a read-only grant, how each result's `reason`/`message` is
 * forwarded rather than discarded) unit-testable without rendering React or
 * mocking a network client (adversarial-review finding: this file previously
 * had ZERO test coverage on the actual controller-construction logic — every
 * existing test here only covered the pure `isWriteCapableGrant` predicate,
 * which is now imported rather than defined here at all — see below).
 *
 * ── Read-only-grant write gating ───────────────────────────────────────────
 *
 * `record_statutory_payment`, `void_statutory_payment`,
 * `generate_upcoming_filings`, `mark_filed`, and `mark_submitted` (#23316) are
 * the five WRITE actions in this controller. Each is `undefined` (hiding the
 * affordance entirely, not
 * just disabling it) when the resolved OAuth grant is not write-capable —
 * see {@link isWriteCapableGrant}'s doc comment (`use_pay_runs.ts`) for the
 * exact rule. This mirrors the sibling C7 (#22327/#22375) pay-run
 * write-gating pattern (`start_pay_run`/`sign_off` becoming `undefined` for
 * a read-only grant) — `isWriteCapableGrant` is now IMPORTED from
 * `use_pay_runs.ts` rather than reimplemented here (C7 had not landed on
 * this branch's ancestry at the time this module was first written, so an
 * earlier version carried its own copy of the same truth table;
 * adversarial-review finding, C9 second round, #22415: now that C7 has
 * genuinely merged, the duplicate is a pure DRY/drift risk with no
 * remaining justification). `is_read_only_grant` is exposed on the
 * controller so `post_run_sections.tsx` can show an explanatory notice
 * rather than a silently-vanished button.
 */
import * as React from 'react';
import { useAuth } from '../lib/auth/auth_context.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import {
  fetchGeneratedPayslip,
  fetchPaymentSourceAccounts,
  fetchSalaryDisbursementFile,
} from '../lib/auth/oauth_pay_run_post_actions.js';
import {
  fetchStatutoryPayments,
  generateUpcomingFilings as generateUpcomingFilingsReal,
  listFilingObligations as listFilingObligationsReal,
  markFiled as markFiledReal,
  markSubmitted as markSubmittedReal,
  recordStatutoryPayment as recordStatutoryPaymentReal,
  voidStatutoryPayment as voidStatutoryPaymentReal,
} from '../lib/auth/oauth_statutory_filing.js';
import { isWriteCapableGrant } from './use_pay_runs.js';
import type { PayRunPostActionsController } from './wagepro_store.js';

/**
 * The real fetch/mutate functions {@link buildPostActionsController} wires
 * into the controller it returns — one field per `PayRunPostActionsController`
 * method that needs a network call. Typed off the real functions themselves
 * (`typeof fetchGeneratedPayslip`, …) rather than hand-duplicating their
 * signatures, so this type can never silently drift from the real
 * implementations it stands in for. A test supplies fakes shaped like these
 * instead of mocking the tRPC client.
 */
export interface PostActionsResolvers {
  generatePayslip: typeof fetchGeneratedPayslip;
  getSalaryDisbursementFile: typeof fetchSalaryDisbursementFile;
  listPaymentSourceAccounts: typeof fetchPaymentSourceAccounts;
  listStatutoryPayments: typeof fetchStatutoryPayments;
  recordStatutoryPayment: typeof recordStatutoryPaymentReal;
  voidStatutoryPayment: typeof voidStatutoryPaymentReal;
  generateUpcomingFilings: typeof generateUpcomingFilingsReal;
  listFilingObligations: typeof listFilingObligationsReal;
  markFiled: typeof markFiledReal;
  markSubmitted: typeof markSubmittedReal;
}

/**
 * Pure, dependency-injected construction of the whole
 * `PayRunPostActionsController` — no React inside. `usePayRunPostActions`
 * below is a thin wrapper that resolves `firm_id`/`entity_id`/`writeCapable`
 * via the real hooks and calls this with the real fetch functions as
 * `resolvers`. Exported and unit-tested directly (adversarial-review
 * finding, C9 second round, #22415 — see module docblock).
 */
export function buildPostActionsController(params: {
  firm_id: string;
  entity_id: string;
  writeCapable: boolean;
  resolvers: PostActionsResolvers;
}): PayRunPostActionsController {
  const { firm_id, entity_id, writeCapable, resolvers } = params;

  return {
    // `...input` spread BEFORE `firm_id`/`entity_id` (not after) so the
    // trusted, grant-derived identity always wins even if a future
    // caller's `input` object happened to carry same-named fields —
    // TS's structural typing wouldn't flag an extra property on a
    // variable already narrowed to this exact input type, so this is a
    // defence-in-depth ordering fix, not a currently-exploitable gap
    // (adversarial-review finding, C8; preserved here for every method).
    generate_payslip: (input) =>
      resolvers.generatePayslip({ ...input, firm_id, entity_id }),
    get_salary_disbursement_file: (input) =>
      resolvers.getSalaryDisbursementFile({ ...input, firm_id, entity_id }),
    list_payment_source_accounts: () =>
      resolvers.listPaymentSourceAccounts({ firm_id, entity_id }),
    // The statutory-payment picker reuses the SAME narrow
    // `payroll.listPaymentSourceAccounts` procedure the disbursement
    // dialog uses (never `account.list` — see `oauth_statutory_filing.ts`'s
    // module docblock), mapped down to the smaller `BankAccountOption`
    // shape (drops `currency_code`, which the statutory dialog never
    // shows).
    list_bank_accounts: () =>
      resolvers
        .listPaymentSourceAccounts({ firm_id, entity_id })
        .then((result) =>
          result.ok
            ? {
                ok: true,
                accounts: result.accounts.map((account) => ({
                  id: account.id,
                  code: account.code,
                  name: account.name,
                })),
              }
            : { ok: false },
        ),
    list_statutory_payments: (pay_run_id) =>
      resolvers
        .listStatutoryPayments({ firm_id, entity_id, pay_run_id })
        .then((result) =>
          result.ok
            ? {
                ok: true,
                computed_due_cents: result.computed_due_cents,
                payments: result.payments,
              }
            : // Forward `entitlement_required`/`error` rather than
              // collapsing to a bare `{ ok: false }` -- an earlier version
              // discarded this, which left `StatutorySection` unable to
              // ever show "buy the payroll add-on" copy for this call
              // (adversarial-review finding).
              { ok: false, reason: result.reason },
        ),
    // Write actions -- `undefined` entirely for a read-only grant, hiding
    // the affordance rather than letting the UI offer a button that would
    // only 403 on click (see module docblock).
    record_statutory_payment: writeCapable
      ? (input) =>
          resolvers.recordStatutoryPayment({ ...input, firm_id, entity_id })
      : undefined,
    void_statutory_payment: writeCapable
      ? (input) =>
          resolvers.voidStatutoryPayment({ ...input, firm_id, entity_id })
      : undefined,
    generate_upcoming_filings: writeCapable
      ? () =>
          resolvers
            .generateUpcomingFilings({ firm_id, entity_id })
            .then((result) =>
              result.ok
                ? {
                    ok: true,
                    inserted: result.inserted,
                    skipped: result.skipped,
                  }
                : { ok: false, reason: result.reason },
            )
      : undefined,
    // Forward the classified `reason` rather than collapsing to a bare
    // `{ ok: false }` -- an earlier version discarded
    // `listFilingObligations`'s own classified reason entirely, which left
    // `FilingSection` unable to ever show the specific
    // `permission_required`/`jurisdiction_unsupported` copy for a LOAD
    // failure, only the generic "could not load" message -- concretely
    // reachable today, since `filingCalendar.list` isn't OAuth-allowlisted
    // until C6/#22325 fully lands (adversarial-review finding, C9 second
    // round, #22415).
    list_filing_obligations: () =>
      resolvers
        .listFilingObligations({ firm_id, entity_id })
        .then((result) =>
          result.ok
            ? { ok: true, obligations: result.obligations }
            : { ok: false, reason: result.reason },
        ),
    mark_filed: writeCapable
      ? (input) =>
          resolvers
            .markFiled({ ...input, firm_id, entity_id })
            .then((result) =>
              result.ok ? { ok: true } : { ok: false, reason: result.reason },
            )
      : undefined,
    // #23316 — legacy `markSubmitted` sibling of `mark_filed`, wired for
    // WagePro parity with the server's still-supported ULID-keyed path.
    // Same read-only-grant hiding + classified-reason forwarding as every
    // other write here.
    mark_submitted: writeCapable
      ? (input) =>
          resolvers
            .markSubmitted({ ...input, firm_id, entity_id })
            .then((result) =>
              result.ok ? { ok: true } : { ok: false, reason: result.reason },
            )
      : undefined,
    is_read_only_grant: !writeCapable,
  };
}

/** The real resolvers `usePayRunPostActions` wires into {@link buildPostActionsController} — the real, OAuth-backed executors, never mocked in production. */
const REAL_RESOLVERS: PostActionsResolvers = {
  generatePayslip: fetchGeneratedPayslip,
  getSalaryDisbursementFile: fetchSalaryDisbursementFile,
  listPaymentSourceAccounts: fetchPaymentSourceAccounts,
  listStatutoryPayments: fetchStatutoryPayments,
  recordStatutoryPayment: recordStatutoryPaymentReal,
  voidStatutoryPayment: voidStatutoryPaymentReal,
  generateUpcomingFilings: generateUpcomingFilingsReal,
  listFilingObligations: listFilingObligationsReal,
  markFiled: markFiledReal,
  markSubmitted: markSubmittedReal,
};

/** The full, real, OAuth-backed post-run actions controller — see module docblock. */
export function usePayRunPostActions():
  | PayRunPostActionsController
  | undefined {
  const entityContext: OAuthEntityContext = useOAuthEntityContext();
  const { grant } = useAuth();

  return React.useMemo(() => {
    if (entityContext.status !== 'ready') return undefined;
    const { firm_id, entity_id } = entityContext;
    return buildPostActionsController({
      firm_id,
      entity_id,
      writeCapable: isWriteCapableGrant(grant),
      resolvers: REAL_RESOLVERS,
    });
  }, [entityContext, grant]);
}
